import { Alert, Checkbox, Input, InputNumber, Select } from 'antd';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import { Rule } from 'antd/es/form';
import styled from 'styled-components';
import { DATE_PICKER_FORMAT } from '../../constants/date';
import { CustomArrayString } from './CustomArrayString';
import { CustomButtonRadio } from './CustomButtonRadio';
import { CustomButtonSelectItem } from './CustomButtonSelectItem';
import CustomColorPicker from './CustomColorPicker';
import { CustomDatePicker } from './CustomDayPicker';
import { CustomTimePicker } from './CustomTimePicker';
import RepeatableField from './RepeatableField';
import { Field } from './SimpleFormTypes';
import { isFieldArrayString, isFieldButtonSelect, isFieldCheckBox, isFieldRepeatable, isFieldSelect } from './utils';

const CheckboxStyled = styled(Checkbox)`
	padding: 10px;
	width: 100%;
`;

const StyledInput = styled(Input)`
	font-size: 14px;
	line-height: 120%;

	min-height: 46px;
`;

const StyledPasswordInput = styled(Input.Password)`
	font-size: 14px;
	line-height: 120%;

	min-height: 46px;
`;

export const formRuleResolver = (field: Field): Rule[] => {
	if (isFieldRepeatable(field)) {
		return [
			{
				// message: 'Chyba',
				validator: (_: any, value: any[] = [], cb) => {
					if (value.length < field.config.minItems) {
						return cb(`Počet položek nesmí být menší než ${field.config.minItems}`);
					}
					if (value.length > field.config.maxItems) {
						return cb(`Počet položek nesmí být větší než ${field.config.maxItems}`);
					}
					return cb();
				},
			},
		];
	}

	return [];
};

export const formInputResolver = (
	field: Field,
	formOptions: { isReadonly: boolean },
	fieldValue?: any,
	onFieldChange?: (value?: any) => void,
	resolverOptions?: { setFormDisabled: (state: boolean) => void; isFormDisabled: boolean },
) => {
	if (field.type === 'custom') {
		return field.render(fieldValue, onFieldChange, resolverOptions);
	}

	const sharedProps = {
		size: 'large' as SizeType,
		disabled: field.disabled || formOptions.isReadonly,
		// required: field.required, // nepouzivat required z duvodu ze se nezobrazi error message, ale použíje se reuqired html
		loading: field.loading,
		'data-testid': `field-${field.key}`,
	};

	if (field.type === 'string' || !field.type) {
		if (field.isTextArea) {
			return <Input.TextArea placeholder={field.placeholder} {...sharedProps} rows={5} />;
		}
		return (
			<StyledInput
				placeholder={field.placeholder}
				type={field.htmlInputType}
				{...sharedProps}
				suffix={field.suffix}
				prefix={field.prefix}
			/>
		);
	}

	if (field.type === 'color-picker') {
		return <CustomColorPicker {...sharedProps} />;
	}

	if (field.type === 'number') {
		return (
			<InputNumber
				placeholder={field.placeholder}
				type={field.htmlInputType}
				controls={false}
				style={{ width: '100%' }}
				{...sharedProps}
				suffix={field.suffix}
				prefix={field.prefix}
			/>
		);
	}

	if (isFieldSelect(field)) {
		if ((field as any).groups) {
			const groups = (field as any).values as {
				value: string;
				key: string;
				group: string;
				disabled?: boolean;
			}[];
			const options = groups.reduce((acc: any, v) => {
				const group = acc.find((g: { label: string }) => g.label === v.group);
				if (group) {
					group.options.push({ label: v.value, value: v.key, disabled: v.disabled });
				} else {
					acc.push({ label: v.group, options: [{ label: v.value, value: v.key, disabled: v.disabled }] });
				}
				return acc;
			}, []);

			return (
				<Select
					style={{ width: '100%' }}
					placeholder={field.placeholder}
					mode={field.mode}
					options={options}
					showSearch
					filterOption={(
						input: string,
						option?: {
							label: string;
							value: string;
							children: React.ReactElement;
							options: { label: string; value: string; children: React.ReactElement }[];
						},
					) => {
						if (option?.options) {
							return false;
						}
						const label = option?.label;
						if (typeof label === 'string') {
							return (label ?? '').toLowerCase().includes(input.toLowerCase());
						}
						return false;
					}}
					{...sharedProps}
				/>
			);
		}

		return (
			<Select
				style={{ width: '100%' }}
				placeholder={field.placeholder}
				mode={field.mode}
				showSearch
				filterOption={(
					input: string,
					option?: { label: string; value: string; children: React.ReactElement },
				) => {
					const children = option?.children;
					if (typeof children === 'string') {
						return ((children as any) ?? '').toLowerCase().includes(input.toLowerCase());
					}
					return false;
				}}
				{...sharedProps}
			>
				{field.values.map(value => (
					<Select.Option key={value.key} value={value.key} disabled={value.disabled}>
						{value.value}
					</Select.Option>
				))}
			</Select>
		);
	}

	if (field.type === 'password') {
		return <StyledPasswordInput placeholder={field.placeholder} {...sharedProps} />;
	}

	if (field.type === 'checkbox-group') {
		return (
			<Checkbox.Group style={{ width: '100%' }} {...sharedProps}>
				{field.values.map(value => (
					<CheckboxStyled value={value.key} key={value.key} disabled={value.disabled}>
						{value.value}
					</CheckboxStyled>
				))}
			</Checkbox.Group>
		);
	}

	if (field.type === 'time-picker') {
		return <CustomTimePicker format="HH:mm" placeholder={field.placeholder} {...sharedProps} />;
	}

	if (field.type === 'day-picker') {
		const disabledDate = (d: any): boolean => {
			if (field.minDate && field.maxDate) {
				return d.isBefore(field.minDate) || d.isAfter(field.maxDate);
			}

			if (field.minDate) {
				return d.isBefore(field.minDate);
			}

			if (field.maxDate) {
				return d.isAfter(field.maxDate);
			}

			return false;
		};

		return (
			<CustomDatePicker
				disabledDate={disabledDate}
				format={DATE_PICKER_FORMAT}
				placeholder={field.placeholder}
				{...sharedProps}
			/>
		);
	}

	if (field.type === 'button-radio') {
		return <CustomButtonRadio values={field.values} value={fieldValue} onChange={onFieldChange} />;
	}

	if (isFieldCheckBox(field)) {
		return <CheckboxStyled {...sharedProps}>{field.node}</CheckboxStyled>;
	}

	if (isFieldButtonSelect(field)) {
		return <CustomButtonSelectItem values={field.values} />;
	}

	if (isFieldArrayString(field)) {
		return <CustomArrayString {...sharedProps} placeholder={field.placeholder} />;
	}

	if (isFieldRepeatable(field)) {
		return <RepeatableField {...field} />;
	}

	return <Alert description={`Chyba konfigurace ${field.key}`} type="error" />;
};
